import type { ReactNode } from 'react';
import { ChevronDown, Trash2 } from 'lucide-react';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import type { ActionColumnAction } from './types';

interface BuildRowMenuItemsArgs {
  hasId: boolean;
  isExpandable: boolean;
  isExpanded: boolean;
  onToggleExpand?: () => void;
  onDelete: () => void;
  callerItems?: ReactNode;
  actions?: ActionColumnAction[];
  expandRowLabel: string;
  collapseRowLabel: string;
  deleteRowLabel: string;
  /** When true, skip the auto-injected expand-toggle menu item — the
   * caller is providing their own expand affordance via `callerItems`. */
  hideExpandItem?: boolean;
}

/**
 * Composes the dropdown body for the leading action column on a clean row.
 *
 * Item order: expand-toggle → caller items (`renderRowMenu`) → delete.
 * The expand-toggle item is auto-injected when `isExpandable && onToggleExpand`
 * is wired; the delete item is auto-injected when `hasId` and `'delete'` is
 * in `actions`. Caller items sit between them so the destructive Delete
 * stays at the bottom by convention.
 *
 * Returns `null` when no items would render — `ActionCell` then falls
 * through to render nothing (clean row, no affordance).
 */
export function buildRowMenuItems({
  hasId,
  isExpandable,
  isExpanded,
  onToggleExpand,
  onDelete,
  callerItems,
  actions,
  expandRowLabel,
  collapseRowLabel,
  deleteRowLabel,
  hideExpandItem,
}: BuildRowMenuItemsArgs): ReactNode {
  const showDelete = hasId && (!actions || actions.includes('delete'));
  const showExpand = isExpandable && !!onToggleExpand && !hideExpandItem;

  if (!showExpand && !callerItems && !showDelete) return null;

  return (
    <>
      {showExpand && (
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand?.();
          }}
        >
          <ChevronDown
            className="h-4 w-4 mr-2 transition-transform"
            style={{ transform: isExpanded ? 'rotate(180deg)' : undefined }}
          />
          {isExpanded ? collapseRowLabel : expandRowLabel}
        </DropdownMenuItem>
      )}
      {callerItems}
      {showDelete && (
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="text-destructive"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          {deleteRowLabel}
        </DropdownMenuItem>
      )}
    </>
  );
}
